import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger
} from '../components/ui/sheet';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from '../components/ui/alert-dialog';
import { jobsAPI, presetsAPI, settingsAPI, materialsAPI, extrasAPI } from '../lib/api';
import { toast } from 'sonner';
import {
    ArrowLeft, Plus, Trash2, Edit2, MapPin, FileDown, Send, CheckCircle2,
    Loader2, ChevronDown, ChevronUp, X, Eye, EyeOff, FileText, Star,
    MessageSquare, Mail, Receipt, Pencil, Package
} from 'lucide-react';

const statusColors = {
    Draft: 'bg-slate-100 text-slate-700',
    Sent: 'bg-blue-100 text-blue-700',
    Accepted: 'bg-green-100 text-green-700',
    Completed: 'bg-emerald-100 text-emerald-700',
    Lost: 'bg-red-100 text-red-700'
};

export const JobDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [job, setJob] = useState(null);
    const [presets, setPresets] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [downloadingInvoicePdf, setDownloadingInvoicePdf] = useState(false);
    const [expandedRooms, setExpandedRooms] = useState({});
    const [showInternalCosts, setShowInternalCosts] = useState(false);

    // Adjustment states
    const [adjSheetOpen, setAdjSheetOpen] = useState(false);
    const [customAdj, setCustomAdj] = useState({ name: '', type: 'fixed', value: 0, reason: '' });
    const [entrySheetOpen, setEntrySheetOpen] = useState(false);
    const [customEntry, setCustomEntry] = useState({ description: '', amount: 0, notes: '' });

    // Quote editing state
    const [editingQuote, setEditingQuote] = useState(false);
    const [quoteLines, setQuoteLines] = useState([]);

    // Invoice state
    const [showInvoiceReview, setShowInvoiceReview] = useState(false);
    const [invoiceLines, setInvoiceLines] = useState([]);

    // Review request state
    const [showReviewDialog, setShowReviewDialog] = useState(false);
    const [reviewMessage, setReviewMessage] = useState('');
    const [reviewLink, setReviewLink] = useState('');

    // Materials order state
    const [materialEstimates, setMaterialEstimates] = useState(null);
    const [loadingMaterials, setLoadingMaterials] = useState(false);
    const [showMaterials, setShowMaterials] = useState(false);

    // Job Extras state
    const [extrasPresets, setExtrasPresets] = useState([]);
    const [jobExtras, setJobExtras] = useState([]);   // [{preset_id, name, amount, enabled}]
    const [savingExtras, setSavingExtras] = useState(false);

    useEffect(() => { fetchJob(); fetchPresets(); fetchExtrasPresets(); }, [id]);

    const fetchJob = async () => {
        try {
            const res = await jobsAPI.get(id);
            setJob(res.data);
            setJobExtras(res.data.job_extras || []);
        } catch {
            toast.error('Job not found');
            navigate('/jobs');
        } finally { setLoading(false); }
    };

    const fetchPresets = async () => {
        try { const res = await presetsAPI.list(); setPresets(res.data); } catch {}
    };

    const fetchExtrasPresets = async () => {
        try {
            const res = await extrasAPI.listPresets();
            setExtrasPresets(res.data);
        } catch {}
    };

    const fmt = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 }).format(v || 0);
    const toggleRoom = (roomId) => setExpandedRooms(p => ({ ...p, [roomId]: !p[roomId] }));

    // Merge presets with job's saved extras — presets not yet in job_extras get default state
    const mergedExtras = extrasPresets.map(preset => {
        const saved = jobExtras.find(e => e.preset_id === preset.id);
        return saved || { preset_id: preset.id, name: preset.name, amount: preset.default_amount, enabled: false };
    });

    const handleExtrasToggle = async (presetId, enabled) => {
        const updated = mergedExtras.map(e =>
            e.preset_id === presetId ? { ...e, enabled } : e
        );
        setJobExtras(updated);
        setSavingExtras(true);
        try {
            const res = await extrasAPI.saveJobExtras(id, updated);
            setJob(res.data);
            setJobExtras(res.data.job_extras || []);
        } catch { toast.error('Failed to save'); }
        finally { setSavingExtras(false); }
    };

    const handleExtrasAmountChange = (presetId, amount) => {
        setJobExtras(mergedExtras.map(e =>
            e.preset_id === presetId ? { ...e, amount: parseFloat(amount) || 0 } : e
        ));
    };

    const handleExtrasAmountBlur = async (presetId) => {
        setSavingExtras(true);
        try {
            const res = await extrasAPI.saveJobExtras(id, mergedExtras);
            setJob(res.data);
            setJobExtras(res.data.job_extras || []);
        } catch { toast.error('Failed to save'); }
        finally { setSavingExtras(false); }
    };

    // Status
    const handleStatusChange = async (newStatus) => {
        try {
            const res = await jobsAPI.update(id, { status: newStatus });
            setJob(res.data);
            toast.success(`Status changed to ${newStatus}`);
        } catch { toast.error('Failed to update status'); }
    };

    // Delete Job
    const handleDeleteJob = async () => {
        try {
            await jobsAPI.delete(id);
            toast.success('Job deleted');
            navigate('/jobs');
        } catch { toast.error('Failed to delete job'); }
    };

    // Labour only toggle
    const handleLabourOnlyToggle = async () => {
        try {
            const res = await jobsAPI.update(id, { labour_only: !job.labour_only });
            setJob(res.data);
            toast.success(res.data.labour_only ? 'Labour only enabled' : 'Labour only disabled');
        } catch { toast.error('Failed to toggle'); }
    };

    // Room CRUD
    const handleDeleteRoom = async (roomId) => {
        try { const res = await jobsAPI.deleteRoom(id, roomId); setJob(res.data); toast.success('Room deleted'); }
        catch { toast.error('Failed to delete room'); }
    };

    // Manual Adjustments
    const handleAddPresetAdj = async (preset) => {
        try {
            const res = await jobsAPI.addAdjustment(id, { name: preset.name, type: preset.type, value: preset.value, reason: preset.default_note || '' });
            setJob(res.data); toast.success('Adjustment added');
        } catch { toast.error('Failed to add adjustment'); }
    };
    const handleAddCustomAdj = async () => {
        if (!customAdj.name || !customAdj.value) { toast.error('Name and value required'); return; }
        try {
            const res = await jobsAPI.addAdjustment(id, customAdj);
            setJob(res.data); setCustomAdj({ name: '', type: 'fixed', value: 0, reason: '' }); setAdjSheetOpen(false);
            toast.success('Adjustment added');
        } catch { toast.error('Failed to add'); }
    };
    const handleDeleteAdj = async (adjId) => {
        try { const res = await jobsAPI.deleteAdjustment(id, adjId); setJob(res.data); toast.success('Removed'); }
        catch { toast.error('Failed to remove'); }
    };

    // Manual Entry for Quote
    const handleAddEntry = async () => {
        if (!customEntry.description || !customEntry.amount) { toast.error('Description and amount required'); return; }
        try {
            const res = await jobsAPI.addAdditionalWork(id, customEntry);
            setJob(res.data); setCustomEntry({ description: '', amount: 0, notes: '' }); setEntrySheetOpen(false);
            toast.success('Entry added');
        } catch { toast.error('Failed to add entry'); }
    };
    const handleDeleteEntry = async (workId) => {
        try { const res = await jobsAPI.deleteAdditionalWork(id, workId); setJob(res.data); toast.success('Removed'); }
        catch { toast.error('Failed to remove'); }
    };

    // Quote line editing
    const startQuoteEdit = () => {
        const lines = [];
        const overrides = job.quote_overrides || [];
        if (overrides.length > 0) {
            overrides.forEach(o => lines.push({ id: o.id, description: o.description, amount: o.amount }));
        } else {
            (job.areas || []).forEach(area => {
                lines.push({ id: area.id, description: area.area_name || area.room_type, amount: area.calculated_cost || 0 });
            });
            (job.additional_works || []).forEach(w => {
                lines.push({ id: w.id, description: w.description, amount: w.amount || 0 });
            });
        }
        setQuoteLines(lines);
        setEditingQuote(true);
    };
    const saveQuoteOverrides = async () => {
        try {
            const res = await jobsAPI.saveQuoteOverrides(id, quoteLines);
            setJob(res.data); setEditingQuote(false); toast.success('Quote lines updated');
        } catch { toast.error('Failed to save'); }
    };

    // Invoice
    const startInvoiceCreation = () => {
        const lines = [];
        const overrides = job.quote_overrides || [];
        if (overrides.length > 0) {
            overrides.forEach(o => lines.push({ description: o.description, amount: o.amount }));
        } else {
            (job.areas || []).forEach(area => {
                lines.push({ description: area.area_name || area.room_type, amount: area.calculated_cost || 0 });
            });
            (job.additional_works || []).forEach(w => {
                lines.push({ description: w.description, amount: w.amount || 0 });
            });
        }
        setInvoiceLines(lines);
        setShowInvoiceReview(true);
    };
    const confirmInvoice = async () => {
        try {
            const res = await jobsAPI.createInvoice(id, invoiceLines);
            setJob(res.data); setShowInvoiceReview(false); toast.success('Invoice created');
        } catch { toast.error('Failed to create invoice'); }
    };
    const handleInvoiceStatus = async (status) => {
        try {
            const res = await jobsAPI.updateInvoiceStatus(id, status);
            setJob(res.data);
            if (status === 'Paid') {
                toast.success('Invoice marked as paid');
                loadReviewMessage();
            } else {
                toast.success(`Invoice marked as ${status}`);
            }
        } catch { toast.error('Failed to update invoice'); }
    };

    // Review request
    const loadReviewMessage = async () => {
        try {
            const res = await settingsAPI.getTemplate();
            const tmpl = res.data;
            const reviewLink = tmpl.review_google_link || '';
            let msg = tmpl.review_template_message || '';
            if (!msg) {
                msg = `Thank you for choosing ${tmpl.company_name || 'us'}! We'd love your feedback on Google:\n${reviewLink || '[Google Review Link]'}`;
            } else {
                msg = msg.replace(/\{link\}/gi, reviewLink || '[Google Review Link]');
                if (reviewLink && !msg.includes(reviewLink)) {
                    msg = msg + '\n\n' + reviewLink;
                }
            }
            setReviewMessage(msg);
            setReviewLink(reviewLink);
            setShowReviewDialog(true);
        } catch {
            setReviewMessage('Thank you for your business! We would appreciate a review.');
            setReviewLink('');
            setShowReviewDialog(true);
        }
    };

    // Materials order
    const loadMaterialEstimates = async () => {
        setLoadingMaterials(true);
        setShowMaterials(true);
        try {
            const res = await materialsAPI.getJobEstimates(id);
            setMaterialEstimates(res.data);
        } catch { toast.error('Failed to load material estimates'); }
        finally { setLoadingMaterials(false); }
    };

    // PDF
    const handleDownloadPDF = async () => {
        setDownloadingPdf(true);
        try {
            const res = await jobsAPI.getQuotePDF(id);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a'); link.href = url;
            link.download = `Quote-${job.quote_number}.pdf`; link.click();
            window.URL.revokeObjectURL(url); toast.success('Quote PDF downloaded');
        } catch { toast.error('Failed to generate PDF'); }
        finally { setDownloadingPdf(false); }
    };

    const handleDownloadInvoicePDF = async () => {
        setDownloadingInvoicePdf(true);
        try {
            const res = await jobsAPI.getInvoicePDF(id);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a'); link.href = url;
            link.download = `Invoice-${job.quote_number}.pdf`; link.click();
            window.URL.revokeObjectURL(url); toast.success('Invoice PDF downloaded');
        } catch { toast.error('Failed to generate invoice PDF'); }
        finally { setDownloadingInvoicePdf(false); }
    };

    const handleRevertToDraft = async () => {
        try {
            const res = await jobsAPI.update(id, { status: 'Draft' });
            setJob(res.data);
            toast.success('Job reverted to Draft — editing unlocked');
        } catch { toast.error('Failed to revert status'); }
    };

    if (loading) return (
        <Layout><div className="max-w-2xl mx-auto p-4 space-y-4">
            <Skeleton className="h-8 w-48" /><Skeleton className="h-40 w-full" /><Skeleton className="h-40 w-full" />
        </div></Layout>
    );
    if (!job) return null;

    const pricing = job.pricing_summary || {};
    const rooms = job.areas || [];
    const adjustments = job.adjustments || [];
    const additionalWorks = job.additional_works || [];
    const addr = job.site_address || {};
    const invoice = job.invoice;

    return (
        <Layout>
            <div className="max-w-2xl mx-auto p-4 pb-24" data-testid="job-detail-page">
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                    <button onClick={() => navigate('/jobs')} data-testid="back-to-jobs"
                        className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="flex-1">
                        <h1 className="font-heading text-xl font-bold text-slate-900" data-testid="quote-number">{job.quote_number}</h1>
                        <p className="text-sm text-slate-500">{job.customer_name}
                            {job.enquiry_source && <span className="ml-2 text-xs text-slate-400">via {job.enquiry_source}</span>}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {job.job_type && job.job_type !== 'Plastering' && (
                            <Badge className={job.job_type === 'Rendering' ? 'bg-orange-100 text-orange-700' : 'bg-purple-100 text-purple-700'}>
                                {job.job_type}
                            </Badge>
                        )}
                        <Badge className={statusColors[job.status] || 'bg-slate-100'} data-testid="job-status">{job.status}</Badge>
                        <button onClick={() => navigate(`/jobs/${id}/edit`)} data-testid="edit-job-details-btn"
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors">
                            <Pencil className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Address + Labour Only */}
                <div className="flex gap-2 mb-4">
                    <Card className="flex-1"><CardContent className="p-3">
                        <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                            <div className="text-sm text-slate-600">
                                {addr.line1 && <div>{addr.line1}</div>}
                                {addr.line2 && <div>{addr.line2}</div>}
                                {(addr.city || addr.postcode) && <div>{addr.city} {addr.postcode}</div>}
                            </div>
                        </div>
                    </CardContent></Card>
                    {job.status === 'Draft' && (
                        <Card className="shrink-0"><CardContent className="p-3">
                            <button onClick={handleLabourOnlyToggle} data-testid="labour-only-toggle"
                                className="flex flex-col items-center gap-1">
                                <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${job.labour_only ? 'bg-red-600' : 'bg-slate-300'}`}>
                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${job.labour_only ? 'translate-x-6' : 'translate-x-1'}`} />
                                </div>
                                <span className="text-[10px] font-semibold text-slate-500 uppercase">Labour Only</span>
                            </button>
                        </CardContent></Card>
                    )}
                    {job.labour_only && job.status !== 'Draft' && (
                        <Card className="shrink-0"><CardContent className="p-3 flex items-center">
                            <Badge className="bg-red-100 text-red-700 text-xs">Labour Only</Badge>
                        </CardContent></Card>
                    )}
                </div>

                {/* ============ ROOMS ============ */}
                <div className="mb-6" data-testid="rooms-section">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-heading text-base font-bold text-slate-900 uppercase tracking-wider">Rooms</h2>
                        {job.status === 'Draft' && (
                            <Button size="sm" onClick={() => navigate(`/jobs/${id}/add-room`)} data-testid="add-room-btn"
                                className="h-8 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold">
                                <Plus className="w-3.5 h-3.5 mr-1" /> Add Room
                            </Button>
                        )}
                    </div>
                    {rooms.length === 0 && (
                        <Card><CardContent className="p-6 text-center text-sm text-slate-400">
                            No rooms surveyed yet. Tap "Add Room" to start.
                        </CardContent></Card>
                    )}
                    <div className="space-y-2">
                        {rooms.map((room) => {
                            const exp = expandedRooms[room.id];
                            const bd = room.cost_breakdown || {};
                            return (
                                <Card key={room.id} data-testid={`room-card-${room.id}`}>
                                    <CardContent className="p-0">
                                        <button onClick={() => toggleRoom(room.id)}
                                            className="w-full p-3 flex items-center justify-between text-left">
                                            <div>
                                                <div className="font-semibold text-sm text-slate-900">{room.area_name || room.room_type}</div>
                                                <div className="text-xs text-slate-500 mt-0.5">
                                                    {room.wall_count || 0} walls &middot; {room.room_height}m
                                                    {bd.total_wall_area_m2 ? ` · ${bd.total_wall_area_m2} m²` : ''}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="font-bold text-sm text-slate-900 tabular-nums">{fmt(room.calculated_cost)}</span>
                                                {exp ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                                            </div>
                                        </button>
                                        {exp && (
                                            <div className="px-3 pb-3 border-t border-slate-100 pt-2">
                                                <div className="space-y-1 text-xs">
                                                    {bd.wall_plastering > 0 && <div className="flex justify-between"><span className="text-slate-600">Wall plastering ({room.all_walls_same ? room.single_wall_work : 'Mixed'})</span><span className="font-medium">{fmt(bd.wall_plastering)}</span></div>}
                                                    {bd.wall_insulation > 0 && <div className="flex justify-between"><span className="text-slate-600">Wall insulation ({(room.insulated_walls||[]).map(i=>`W${i+1}`).join(', ')})</span><span className="font-medium">{fmt(bd.wall_insulation)}</span></div>}
                                                    {bd.ceiling_plastering > 0 && <div className="flex justify-between"><span className="text-slate-600">Ceiling ({room.ceiling_work_type}{bd.ceiling_area_m2 ? ` · ${bd.ceiling_area_m2}m²` : ''})</span><span className="font-medium">{fmt(bd.ceiling_plastering)}</span></div>}
                                                    {bd.ceiling_insulation > 0 && <div className="flex justify-between"><span className="text-slate-600">Ceiling insulation</span><span className="font-medium">{fmt(bd.ceiling_insulation)}</span></div>}
                                                    {bd.preparation > 0 && <div className="flex justify-between"><span className="text-slate-600">Preparation ({room.prep_hours}hrs)</span><span className="font-medium">{fmt(bd.preparation)}</span></div>}
                                                    {bd.plaster_beads > 0 && <div className="flex justify-between"><span className="text-slate-600">Plaster beads ({room.plaster_beads})</span><span className="font-medium">{fmt(bd.plaster_beads)}</span></div>}
                                                    {bd.additional_items > 0 && <div className="flex justify-between"><span className="text-slate-600">Additional items</span><span className="font-medium">{fmt(bd.additional_items)}</span></div>}
                                                </div>

                                                {/* Internal Costs View */}
                                                {showInternalCosts && (bd.labour_total > 0 || bd.materials_total > 0) && (
                                                    <div className="mt-2 pt-2 border-t border-dashed border-slate-200">
                                                        <p className="text-[10px] font-bold text-amber-600 uppercase mb-1">Internal Use Only</p>
                                                        <div className="space-y-0.5 text-xs">
                                                            <div className="flex justify-between"><span className="text-amber-700">Labour</span><span className="font-medium text-amber-700">{fmt(bd.labour_total)}</span></div>
                                                            <div className="flex justify-between"><span className="text-amber-700">Materials</span><span className="font-medium text-amber-700">{fmt(bd.materials_total)}</span></div>
                                                        </div>
                                                    </div>
                                                )}

                                                {room.notes && <p className="text-xs text-slate-500 mt-2 italic">{room.notes}</p>}
                                                {job.status === 'Draft' && (
                                                    <div className="flex gap-2 mt-3 pt-2 border-t border-slate-100">
                                                        <Button size="sm" variant="outline" onClick={() => navigate(`/jobs/${id}/edit-room/${room.id}`)}
                                                            className="h-7 text-xs"><Edit2 className="w-3 h-3 mr-1" /> Edit</Button>
                                                        <AlertDialog>
                                                            <AlertDialogTrigger asChild>
                                                                <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50">
                                                                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                                                                </Button>
                                                            </AlertDialogTrigger>
                                                            <AlertDialogContent>
                                                                <AlertDialogHeader><AlertDialogTitle>Delete Room?</AlertDialogTitle>
                                                                    <AlertDialogDescription>This will remove this room and recalculate the quote.</AlertDialogDescription></AlertDialogHeader>
                                                                <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
                                                                    <AlertDialogAction onClick={() => handleDeleteRoom(room.id)} className="bg-red-600">Delete</AlertDialogAction></AlertDialogFooter>
                                                            </AlertDialogContent>
                                                        </AlertDialog>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </div>

                {/* ============ JOB EXTRAS ============ */}
                <div className="mb-6" data-testid="job-extras-section">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <h2 className="font-heading text-base font-bold text-slate-900 uppercase tracking-wider">Site Extras</h2>
                            <p className="text-[11px] text-slate-500 mt-0.5">Tick to include as line items on the quote</p>
                        </div>
                        {savingExtras && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
                    </div>
                    {extrasPresets.length === 0 ? (
                        <Card><CardContent className="p-3 text-center text-xs text-slate-400">No extras configured — add them in Settings → Job Extras.</CardContent></Card>
                    ) : (
                        <Card className="bg-white border-slate-200">
                            <CardContent className="p-0">
                                <div className="divide-y divide-slate-100">
                                    {mergedExtras.map(extra => (
                                        <div key={extra.preset_id} className="flex items-center gap-3 px-4 py-3">
                                            <button
                                                onClick={() => job.status === 'Draft' && handleExtrasToggle(extra.preset_id, !extra.enabled)}
                                                disabled={job.status !== 'Draft'}
                                                data-testid={`extra-toggle-${extra.preset_id}`}
                                                className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${extra.enabled ? 'bg-red-600 border-red-600' : 'border-slate-300 bg-white'} ${job.status === 'Draft' ? 'cursor-pointer hover:border-red-400' : 'opacity-50 cursor-default'}`}
                                            >
                                                {extra.enabled && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                            </button>
                                            <span className={`flex-1 text-sm font-medium ${extra.enabled ? 'text-slate-900' : 'text-slate-400'}`}>{extra.name}</span>
                                            <div className="relative w-28 shrink-0">
                                                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">£</span>
                                                <Input
                                                    type="number" step="0.01" min="0"
                                                    value={extra.amount}
                                                    onChange={e => handleExtrasAmountChange(extra.preset_id, e.target.value)}
                                                    onBlur={() => handleExtrasAmountBlur(extra.preset_id)}
                                                    disabled={!extra.enabled || job.status !== 'Draft'}
                                                    className={`h-9 pl-6 text-sm text-right border-slate-300 ${!extra.enabled ? 'opacity-40' : ''}`}
                                                    data-testid={`extra-amount-${extra.preset_id}`}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* Internal Costs Toggle */}
                <div className="mb-4">
                    <button onClick={() => setShowInternalCosts(!showInternalCosts)} data-testid="toggle-internal-costs"
                        className="flex items-center gap-2 text-xs font-semibold text-amber-600 hover:text-amber-700 transition-colors">
                        {showInternalCosts ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        {showInternalCosts ? 'Hide' : 'Show'} Internal Costs (Labour/Materials)
                    </button>
                </div>

                {/* ============ MANUAL ADJUSTMENTS (Internal) ============ */}
                <div className="mb-6" data-testid="manual-adjustments-section">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <h2 className="font-heading text-base font-bold text-slate-900 uppercase tracking-wider">Manual Adjustments</h2>
                            <p className="text-[11px] text-slate-500 mt-0.5">Internal only — distributed across quote, not shown to client</p>
                        </div>
                        {job.status === 'Draft' && (
                            <>
                                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setAdjSheetOpen(true)} data-testid="add-adjustment-btn">
                                    <Plus className="w-3.5 h-3.5 mr-1" /> Add
                                </Button>
                                <Dialog open={adjSheetOpen} onOpenChange={setAdjSheetOpen}>
                                    <DialogContent className="max-w-md">
                                        <DialogHeader><DialogTitle>Add Manual Adjustment</DialogTitle></DialogHeader>
                                        <div className="space-y-4 mt-2">
                                            {presets.length > 0 && (
                                                <div>
                                                    <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Presets</p>
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {presets.map(p => (
                                                            <button key={p.id} onClick={() => { handleAddPresetAdj(p); setAdjSheetOpen(false); }}
                                                                className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-medium text-slate-700">
                                                                {p.name} ({p.type === 'fixed' ? `£${p.value}` : `${p.value}%`})
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            <div className="border-t pt-4 space-y-2">
                                                <Input placeholder="Adjustment name" value={customAdj.name} onChange={(e) => setCustomAdj({ ...customAdj, name: e.target.value })} data-testid="adj-name-input" />
                                                <div className="flex gap-2">
                                                    <select value={customAdj.type} onChange={(e) => setCustomAdj({ ...customAdj, type: e.target.value })}
                                                        className="h-10 px-3 border rounded-lg text-sm" data-testid="adj-type-select">
                                                        <option value="fixed">Fixed (£)</option><option value="percent">Percent (%)</option>
                                                    </select>
                                                    <Input type="number" placeholder="Value" value={customAdj.value} onChange={(e) => setCustomAdj({ ...customAdj, value: parseFloat(e.target.value) || 0 })} data-testid="adj-value-input" />
                                                </div>
                                                <Input placeholder="Reason (optional)" value={customAdj.reason} onChange={(e) => setCustomAdj({ ...customAdj, reason: e.target.value })} />
                                                <Button onClick={handleAddCustomAdj} className="w-full bg-slate-900" data-testid="save-adj-btn">Add Adjustment</Button>
                                            </div>
                                        </div>
                                    </DialogContent>
                                </Dialog>
                            </>
                        )}
                    </div>
                    {adjustments.length === 0 ? (
                        <Card><CardContent className="p-3 text-center text-xs text-slate-400">No manual adjustments.</CardContent></Card>
                    ) : (
                        <div className="space-y-1.5">{adjustments.map(adj => (
                            <Card key={adj.id}><CardContent className="p-2.5 flex items-center justify-between">
                                <div><div className="text-sm font-medium text-slate-800">{adj.name}</div>
                                    {adj.reason && <div className="text-[11px] text-slate-500">{adj.reason}</div>}</div>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-semibold text-slate-700 tabular-nums">{adj.type === 'fixed' ? fmt(adj.value) : `${adj.value}%`}</span>
                                    {job.status === 'Draft' && <button onClick={() => handleDeleteAdj(adj.id)} className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>}
                                </div>
                            </CardContent></Card>
                        ))}</div>
                    )}
                </div>

                {/* ============ MANUAL ENTRY FOR QUOTE (Client-facing) ============ */}
                <div className="mb-6" data-testid="manual-entry-section">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <h2 className="font-heading text-base font-bold text-slate-900 uppercase tracking-wider">Manual Entry for Quote</h2>
                            <p className="text-[11px] text-slate-500 mt-0.5">Line items shown on the client quote</p>
                        </div>
                        {job.status === 'Draft' && (
                            <>
                                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setEntrySheetOpen(true)} data-testid="add-entry-btn">
                                    <Plus className="w-3.5 h-3.5 mr-1" /> Add
                                </Button>
                                <Dialog open={entrySheetOpen} onOpenChange={setEntrySheetOpen}>
                                    <DialogContent className="max-w-md">
                                        <DialogHeader><DialogTitle>Add Quote Line Item</DialogTitle></DialogHeader>
                                        <div className="space-y-3 mt-2">
                                            <Input placeholder="Description" value={customEntry.description} onChange={(e) => setCustomEntry({ ...customEntry, description: e.target.value })} data-testid="entry-desc-input" />
                                            <Input type="number" placeholder="Amount (£)" value={customEntry.amount} onChange={(e) => setCustomEntry({ ...customEntry, amount: parseFloat(e.target.value) || 0 })} data-testid="entry-amount-input" />
                                            <Input placeholder="Notes (optional)" value={customEntry.notes} onChange={(e) => setCustomEntry({ ...customEntry, notes: e.target.value })} />
                                            <Button onClick={handleAddEntry} className="w-full bg-slate-900" data-testid="save-entry-btn">Add to Quote</Button>
                                        </div>
                                    </DialogContent>
                                </Dialog>
                            </>
                        )}
                    </div>
                    {additionalWorks.length === 0 ? (
                        <Card><CardContent className="p-3 text-center text-xs text-slate-400">No manual entries.</CardContent></Card>
                    ) : (
                        <div className="space-y-1.5">{additionalWorks.map(work => (
                            <Card key={work.id}><CardContent className="p-2.5 flex items-center justify-between">
                                <div><div className="text-sm font-medium text-slate-800">{work.description}</div>
                                    {work.notes && <div className="text-[11px] text-slate-500">{work.notes}</div>}</div>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-semibold text-slate-700 tabular-nums">{fmt(work.amount)}</span>
                                    {job.status === 'Draft' && <button onClick={() => handleDeleteEntry(work.id)} className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>}
                                </div>
                            </CardContent></Card>
                        ))}</div>
                    )}
                </div>

                {/* ============ PRICING SUMMARY ============ */}
                <Card className="mb-6" data-testid="pricing-summary">
                    <CardContent className="p-4">
                        <h2 className="font-heading text-base font-bold text-slate-900 uppercase tracking-wider mb-3">Quote Total</h2>
                        <div className="space-y-2 text-sm">
                            {pricing.raw_rooms_total > 0 ? (
                                <>
                                    <div className="flex justify-between"><span className="text-slate-600">Rooms subtotal</span><span className="font-medium tabular-nums">{fmt(pricing.raw_rooms_total)}</span></div>
                                    {pricing.waste_amount > 0 && (
                                        <div className="flex justify-between text-xs"><span className="text-slate-500 pl-3">+ Waste uplift ({pricing.waste_percent}%)</span><span className="text-slate-500 tabular-nums">+{fmt(pricing.waste_amount)}</span></div>
                                    )}
                                    <div className="flex justify-between"><span className="text-slate-600">Subtotal (inc. waste)</span><span className="font-medium tabular-nums">{fmt(pricing.subtotal)}</span></div>
                                </>
                            ) : (
                                <div className="flex justify-between"><span className="text-slate-600">Rooms subtotal (inc. waste)</span><span className="font-medium tabular-nums">{fmt(pricing.subtotal)}</span></div>
                            )}
                            {showInternalCosts && (
                                <>
                                    <div className="flex justify-between text-xs"><span className="text-amber-600 pl-3">— Labour</span><span className="text-amber-600 tabular-nums">{fmt(pricing.labour_total)}</span></div>
                                    <div className="flex justify-between text-xs"><span className="text-amber-600 pl-3">— Materials</span><span className="text-amber-600 tabular-nums">{fmt(pricing.materials_total)}</span></div>
                                </>
                            )}
                            {pricing.internal_adjustments_total !== 0 && <div className="flex justify-between"><span className="text-slate-600">Manual adjustments (internal)</span><span className="font-medium tabular-nums">{fmt(pricing.internal_adjustments_total)}</span></div>}
                            {pricing.additional_works_total > 0 && <div className="flex justify-between"><span className="text-slate-600">Manual entries for quote</span><span className="font-medium tabular-nums">{fmt(pricing.additional_works_total)}</span></div>}
                            {pricing.extras_total > 0 && <div className="flex justify-between"><span className="text-slate-600">Site extras</span><span className="font-medium tabular-nums">{fmt(pricing.extras_total)}</span></div>}
                            {pricing.minimum_charge_applied && <div className="text-xs text-red-600 font-medium">Minimum charge applied</div>}
                            {pricing.vat_mode === 'added' && <div className="flex justify-between"><span className="text-slate-600">VAT ({pricing.vat_rate}%)</span><span className="font-medium tabular-nums">{fmt(pricing.vat_amount)}</span></div>}
                            <div className="flex justify-between pt-2 border-t border-slate-200">
                                <span className="font-bold text-slate-900">Total</span>
                                <span className="font-bold text-lg text-red-600 tabular-nums" data-testid="quote-total">{fmt(pricing.total)}</span>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* ============ QUOTE LINE EDITING ============ */}
                {editingQuote && (
                    <Card className="mb-6 border-blue-200 bg-blue-50/30" data-testid="quote-editor">
                        <CardContent className="p-4">
                            <h2 className="font-heading text-base font-bold text-slate-900 uppercase tracking-wider mb-3">Edit Quote Lines</h2>
                            <div className="space-y-2">
                                {quoteLines.map((line, i) => (
                                    <div key={i} className="flex gap-2 items-center">
                                        <Input value={line.description} onChange={(e) => { const n = [...quoteLines]; n[i].description = e.target.value; setQuoteLines(n); }}
                                            className="flex-1 h-9 text-sm" data-testid={`quote-line-desc-${i}`} />
                                        <div className="relative w-28">
                                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">£</span>
                                            <Input type="number" value={line.amount} onChange={(e) => { const n = [...quoteLines]; n[i].amount = parseFloat(e.target.value) || 0; setQuoteLines(n); }}
                                                className="h-9 text-sm pl-5" data-testid={`quote-line-amt-${i}`} />
                                        </div>
                                        <button onClick={() => setQuoteLines(quoteLines.filter((_, j) => j !== i))}
                                            className="w-7 h-7 flex items-center justify-center rounded text-slate-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
                                    </div>
                                ))}
                                <div className="flex justify-between items-center pt-2 border-t">
                                    <span className="font-bold text-sm">New Total: {fmt(quoteLines.reduce((s, l) => s + (l.amount || 0), 0))}</span>
                                    <div className="flex gap-2">
                                        <Button size="sm" variant="outline" onClick={() => setEditingQuote(false)} className="h-8 text-xs">Cancel</Button>
                                        <Button size="sm" onClick={saveQuoteOverrides} className="h-8 text-xs bg-blue-600 hover:bg-blue-700" data-testid="save-quote-lines">Save Changes</Button>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* ============ INVOICE SECTION ============ */}
                {invoice && (
                    <Card className="mb-6 border-emerald-200" data-testid="invoice-section">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-3">
                                <h2 className="font-heading text-base font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                                    <Receipt className="w-4 h-4" /> Invoice
                                </h2>
                                <Badge className={invoice.status === 'Paid' ? 'bg-green-100 text-green-700' : invoice.status === 'Sent' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}>
                                    {invoice.status}
                                </Badge>
                            </div>
                            <div className="space-y-1 text-sm mb-3">
                                {invoice.line_items?.map((item, i) => (
                                    <div key={i} className="flex justify-between"><span className="text-slate-600">{item.description}</span><span className="font-medium tabular-nums">{fmt(item.amount)}</span></div>
                                ))}
                                <div className="flex justify-between pt-2 border-t">
                                    <span className="font-bold">Invoice Total</span><span className="font-bold text-emerald-600 tabular-nums">{fmt(invoice.total)}</span>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                {invoice.status === 'Draft' && <Button size="sm" onClick={() => handleInvoiceStatus('Sent')} className="h-8 text-xs bg-blue-600"><Send className="w-3 h-3 mr-1" /> Mark Sent</Button>}
                                {invoice.status === 'Sent' && <Button size="sm" onClick={() => handleInvoiceStatus('Paid')} className="h-8 text-xs bg-green-600"><CheckCircle2 className="w-3 h-3 mr-1" /> Mark Paid</Button>}
                                {invoice.status === 'Paid' && <Button size="sm" variant="outline" onClick={loadReviewMessage} className="h-8 text-xs"><Star className="w-3 h-3 mr-1" /> Request Review</Button>}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* ============ INVOICE REVIEW SCREEN ============ */}
                {showInvoiceReview && (
                    <Card className="mb-6 border-emerald-300 bg-emerald-50/30" data-testid="invoice-review">
                        <CardContent className="p-4">
                            <h2 className="font-heading text-base font-bold text-slate-900 uppercase tracking-wider mb-3">Review Invoice</h2>
                            <p className="text-xs text-slate-500 mb-3">Edit line items before finalising the invoice.</p>
                            <div className="space-y-2">
                                {invoiceLines.map((line, i) => (
                                    <div key={i} className="flex gap-2 items-center">
                                        <Input value={line.description} onChange={(e) => { const n = [...invoiceLines]; n[i].description = e.target.value; setInvoiceLines(n); }}
                                            className="flex-1 h-9 text-sm" data-testid={`inv-line-desc-${i}`} />
                                        <div className="relative w-28">
                                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">£</span>
                                            <Input type="number" value={line.amount} onChange={(e) => { const n = [...invoiceLines]; n[i].amount = parseFloat(e.target.value) || 0; setInvoiceLines(n); }}
                                                className="h-9 text-sm pl-5" data-testid={`inv-line-amt-${i}`} />
                                        </div>
                                    </div>
                                ))}
                                <div className="flex justify-between items-center pt-3 border-t">
                                    <span className="font-bold text-sm">Invoice Total: {fmt(invoiceLines.reduce((s, l) => s + (l.amount || 0), 0))}</span>
                                    <div className="flex gap-2">
                                        <Button size="sm" variant="outline" onClick={() => setShowInvoiceReview(false)} className="h-8 text-xs">Cancel</Button>
                                        <Button size="sm" onClick={confirmInvoice} className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-invoice-btn">Confirm Invoice</Button>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* ============ GOOGLE REVIEW REQUEST DIALOG ============ */}
                {showReviewDialog && (
                    <Card className="mb-6 border-yellow-200 bg-yellow-50/30" data-testid="review-dialog">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <Star className="w-5 h-5 text-yellow-500" />
                                <h2 className="font-heading text-base font-bold text-slate-900">Review Request</h2>
                            </div>
                            <div className="bg-white border rounded-lg p-3 mb-3">
                                <p className="text-sm text-slate-700 whitespace-pre-wrap">{reviewMessage}</p>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                <Button size="sm" variant="outline" className="flex-1 h-9 text-xs" data-testid="copy-review-msg-btn"
                                    onClick={() => {
                                        navigator.clipboard.writeText(reviewMessage);
                                        toast.success('Message copied to clipboard');
                                    }}>
                                    <MessageSquare className="w-3 h-3 mr-1" /> Copy Message
                                </Button>
                                {reviewLink && (
                                    <Button size="sm" className="flex-1 h-9 text-xs bg-yellow-500 hover:bg-yellow-600 text-white" data-testid="open-review-link-btn"
                                        onClick={() => window.open(reviewLink, '_blank')}>
                                        <Star className="w-3 h-3 mr-1" /> Open Review Link
                                    </Button>
                                )}
                                {job.customer_email && (
                                    <Button size="sm" variant="outline" className="flex-1 h-9 text-xs" data-testid="send-email-btn"
                                        onClick={() => {
                                            const subject = encodeURIComponent('We would love your feedback!');
                                            const body = encodeURIComponent(reviewMessage);
                                            window.open(`mailto:${job.customer_email}?subject=${subject}&body=${body}`, '_blank');
                                        }}>
                                        <Mail className="w-3 h-3 mr-1" /> Send Email
                                    </Button>
                                )}
                            </div>
                            <button onClick={() => setShowReviewDialog(false)} className="w-full text-center text-xs text-slate-400 mt-2 hover:text-slate-600">Dismiss</button>
                        </CardContent>
                    </Card>
                )}

                {/* ============ MATERIALS ORDER ============ */}
                <div className="mb-6" data-testid="materials-order-section">
                    <button onClick={() => showMaterials ? setShowMaterials(false) : loadMaterialEstimates()}
                        className="flex items-center gap-2 text-xs font-semibold text-slate-600 hover:text-slate-900 transition-colors mb-2">
                        <Package className="w-3.5 h-3.5" />
                        {showMaterials ? 'Hide' : 'Show'} Materials Order
                        {!showMaterials && <span className="text-slate-400 font-normal">(estimated quantities)</span>}
                    </button>
                    {showMaterials && (
                        <Card className="border-slate-200" data-testid="materials-order-card">
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h2 className="font-heading text-base font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                                        <Package className="w-4 h-4" /> Materials Order
                                    </h2>
                                    {materialEstimates?.estimates?.length > 0 && (
                                        <button onClick={() => window.print()} className="text-xs text-slate-500 hover:text-slate-700 underline">Print</button>
                                    )}
                                </div>
                                {loadingMaterials ? (
                                    <div className="flex items-center gap-2 text-sm text-slate-400 py-4 justify-center">
                                        <Loader2 className="w-4 h-4 animate-spin" /> Calculating...
                                    </div>
                                ) : materialEstimates?.message ? (
                                    <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">{materialEstimates.message}</p>
                                ) : materialEstimates?.estimates?.length === 0 ? (
                                    <p className="text-sm text-slate-400 text-center py-4">No material compositions set up for this job's work types.<br /><span className="text-xs">Set up compositions in Settings → Materials.</span></p>
                                ) : (
                                    <>
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm border-collapse">
                                                <thead>
                                                    <tr className="border-b border-slate-200">
                                                        <th className="text-left text-xs font-bold text-slate-500 uppercase pb-2 pr-3">Material</th>
                                                        <th className="text-right text-xs font-bold text-slate-500 uppercase pb-2 px-2">Exact</th>
                                                        <th className="text-right text-xs font-bold text-slate-500 uppercase pb-2 px-2">Order</th>
                                                        <th className="text-right text-xs font-bold text-slate-500 uppercase pb-2 pl-2">Cost</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {materialEstimates.estimates.map((est, i) => (
                                                        <tr key={i} className="border-b border-slate-100">
                                                            <td className="py-2 pr-3">
                                                                <div className="font-medium text-slate-800 text-xs">{est.name}</div>
                                                                {est.category && <div className="text-[10px] text-slate-400">{est.category}</div>}
                                                            </td>
                                                            <td className="py-2 px-2 text-right text-xs text-slate-500 tabular-nums">{est.quantity_exact} {est.unit}</td>
                                                            <td className="py-2 px-2 text-right tabular-nums">
                                                                <span className="font-bold text-slate-900 text-sm">{est.quantity_order}</span>
                                                                <span className="text-xs text-slate-400 ml-1">{est.unit}</span>
                                                            </td>
                                                            <td className="py-2 pl-2 text-right text-xs text-slate-600 tabular-nums">{fmt(est.total_cost)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                <tfoot>
                                                    <tr className="border-t-2 border-slate-300">
                                                        <td colSpan="3" className="pt-2 font-bold text-sm text-slate-900">Est. Materials Total</td>
                                                        <td className="pt-2 text-right font-bold text-sm text-slate-900 tabular-nums">{fmt(materialEstimates.total_materials_cost)}</td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                        <p className="text-[10px] text-slate-400 mt-2">Order quantities rounded up to whole units. Wastage included in calculations.</p>
                                        {/* Labour hours */}
                                        {materialEstimates.labour_estimates?.length > 0 && (
                                            <div className="mt-3 pt-3 border-t border-blue-100">
                                                <p className="text-xs font-bold text-blue-700 uppercase mb-2">Estimated Labour Hours</p>
                                                <div className="space-y-1">
                                                    {materialEstimates.labour_estimates.map((le, i) => (
                                                        <div key={i} className="flex justify-between text-xs">
                                                            <span className="text-slate-600">{le.display_name}</span>
                                                            <span className="font-semibold text-blue-700 tabular-nums">{le.hours} hrs</span>
                                                        </div>
                                                    ))}
                                                    <div className="flex justify-between text-sm font-bold border-t border-blue-200 pt-1 mt-1">
                                                        <span className="text-blue-800">Total Labour Hours</span>
                                                        <span className="text-blue-800 tabular-nums">{materialEstimates.total_labour_hours} hrs</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* ============ ACTIONS ============ */}
                <div className="space-y-2" data-testid="job-actions">
                    {/* PDF Downloads */}
                    <div className="flex gap-2">
                        <Button onClick={handleDownloadPDF} disabled={downloadingPdf} className="flex-1 bg-slate-900 hover:bg-slate-800" data-testid="download-pdf-btn">
                            {downloadingPdf ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileDown className="w-4 h-4 mr-2" />}
                            Quote PDF
                        </Button>
                        {invoice && (
                            <Button onClick={handleDownloadInvoicePDF} disabled={downloadingInvoicePdf} className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="download-invoice-pdf-btn">
                                {downloadingInvoicePdf ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Receipt className="w-4 h-4 mr-2" />}
                                Invoice PDF
                            </Button>
                        )}
                    </div>
                    {!editingQuote && (
                        <Button onClick={startQuoteEdit} variant="outline" className="w-full" data-testid="edit-quote-btn">
                            <FileText className="w-4 h-4 mr-2" /> Edit Quote Lines
                        </Button>
                    )}
                    {!invoice && job.status !== 'Draft' && (
                        <Button onClick={startInvoiceCreation} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="convert-to-invoice-btn">
                            <Receipt className="w-4 h-4 mr-2" /> Convert to Invoice
                        </Button>
                    )}
                    {job.status === 'Draft' && <Button onClick={() => handleStatusChange('Sent')} variant="outline" className="w-full border-blue-200 text-blue-700 hover:bg-blue-50" data-testid="mark-sent-btn"><Send className="w-4 h-4 mr-2" /> Mark as Sent</Button>}
                    {job.status === 'Sent' && <Button onClick={() => handleStatusChange('Accepted')} variant="outline" className="w-full border-green-200 text-green-700 hover:bg-green-50"><CheckCircle2 className="w-4 h-4 mr-2" /> Mark as Accepted</Button>}
                    {job.status === 'Accepted' && <Button onClick={() => handleStatusChange('Completed')} variant="outline" className="w-full border-emerald-200 text-emerald-700 hover:bg-emerald-50"><CheckCircle2 className="w-4 h-4 mr-2" /> Mark as Completed</Button>}

                    {/* Revert to Draft */}
                    {job.status !== 'Draft' && (
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <Button variant="outline" className="w-full border-amber-200 text-amber-700 hover:bg-amber-50" data-testid="revert-to-draft-btn">
                                    Revert to Draft (Unlock Editing)
                                </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle>Revert to Draft?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        This will change the status back to Draft and unlock all editing. You can re-mark it as Sent when you're done.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={handleRevertToDraft} className="bg-amber-600 hover:bg-amber-700">
                                        Revert to Draft
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    )}

                    {/* Delete Job */}
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button variant="outline" className="w-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 mt-2" data-testid="delete-job-btn">
                                <Trash2 className="w-4 h-4 mr-2" /> Delete Job
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Delete {job.quote_number}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    This will permanently delete this job and all its rooms, adjustments, and invoice data. This cannot be undone.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={handleDeleteJob} className="bg-red-600 hover:bg-red-700">
                                    Delete Job
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </div>
        </Layout>
    );
};
